import React from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { theme } from '../styles/theme';
import { SHOW_LANG_TOGGLE, Lang } from '../config/locale';

const LandingFont = createGlobalStyle`
  @import url('https://cdn.jsdelivr.net/gh/fonts-archive/Cafe24Ssurround/subsets/Cafe24Ssurround-dynamic-subset.css');
`;

const FONT = "'Cafe24Ssurround', sans-serif";

interface LandingPageProps {
  onStartCustomize: () => void;
  lang: Lang;
  onLangChange: (lang: Lang) => void;
}

const HL_INTERVAL = 0.12; // 주황 글자 간 딜레이(초)
const CYCLE = 2.5;        // 전체 애니메이션 주기(초)

const LandingPage: React.FC<LandingPageProps> = ({ onStartCustomize, lang, onLangChange }) => {
  const isKo = lang === 'ko';

  const renderTitle = () => {
    if (isKo) {
      const hlStr = '압도적 성능.';
      const hlChars = hlStr.split('').map((char, i) => (
        <HlChar key={i} $delay={i * HL_INTERVAL}>
          {char === ' ' ? '\u00A0' : char}
        </HlChar>
      ));
      return (
        <>
          <WhiteText>완벽한 제어,{'\u00A0'}</WhiteText>
          {hlChars}
        </>
      );
    }

    const enHlStr = 'Overwhelming Power.';
    const enHlChars = enHlStr.split('').map((char, i) => (
      <HlChar key={i} $delay={i * 0.06}>
        {char === ' ' ? '\u00A0' : char}
      </HlChar>
    ));
    return (
      <>
        <WhiteText>Perfect Control,</WhiteText>
        <br />
        {enHlChars}
      </>
    );
  };

  return (
    <Wrapper>
      <LandingFont />
      <GrayscaleFilter />
      <DarkOverlay />

      <Header>
        <Inner>
          <LogoText>{isKo ? '우수건설기계' : 'Woosu Machinery'}</LogoText>
          <RightArea>
            {/* 2026-09-18 영문에서 제거, 한국어 화면에만 유지 */}
            {isKo && (
              <NavLink href="https://우수건설기계.com" target="_blank" rel="noopener noreferrer">
                전체모델보기
              </NavLink>
            )}
            {SHOW_LANG_TOGGLE && (
              <LangToggle>
                <LangBtn $active={lang === 'ko'} onClick={() => onLangChange('ko')}>KR</LangBtn>
                <LangBtn $active={lang === 'en'} onClick={() => onLangChange('en')}>EN</LangBtn>
              </LangToggle>
            )}
          </RightArea>
        </Inner>
      </Header>

      <Content>
        <HeroTitle style={{ fontFamily: FONT }}>
          {renderTitle()}
        </HeroTitle>
        <HeroDesc style={{ fontFamily: FONT }}>
          {isKo
            ? '현장에 최적화된 장비를 지금 바로 구성하세요.'
            : 'Configure the right equipment for your site.'}
        </HeroDesc>
        <CustomizeBtn style={{ fontFamily: FONT }} onClick={onStartCustomize}>
          {isKo ? '견적 시작하기' : 'Get Started'}
        </CustomizeBtn>
      </Content>
    </Wrapper>
  );
};

/* ───── Animations ───── */

// 흰색 텍스트: 부드러운 sweep
const titleSweep = keyframes`
  0% { background-position: 200% center; }
  100% { background-position: -200% center; }
`;

// 주황 글자: 커졌다 줄어듦 + 색상 밝아짐
const hlCharPop = keyframes`
  0%, 30% { transform: scale(1); color: #F97316; }
  45% { transform: scale(1.15); color: #ffe0a0; }
  60%, 100% { transform: scale(1); color: #F97316; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.5); }
  50% { box-shadow: 0 0 24px 8px rgba(249, 115, 22, 0.3); }
`;

/* ───── Styled Components ───── */

const Wrapper = styled.div`
  min-height: 100vh;
  background: #1a1a1a;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const DarkOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  pointer-events: none;
`;

const GrayscaleFilter = styled.div`
  position: absolute;
  inset: 0;
  background: url('/bg_landing.jpg') center / cover no-repeat;
  filter: grayscale(100%) blur(3px);
  pointer-events: none;
`;

const Header = styled.header`
  position: relative;
  z-index: 10;
  height: ${theme.layout.headerHeight};
`;

const Inner = styled.div`
  max-width: ${theme.layout.maxWidth};
  margin: 0 auto;
  padding: 0 40px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LogoText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.5px;
`;

const RightArea = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const NavLink = styled.a`
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: #ffffff;
  }
`;

const LangToggle = styled.div`
  display: flex;
  gap: 4px;
  font-size: 13px;
`;

const LangBtn = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  font-weight: ${({ $active }) => $active ? 700 : 400};
  color: ${({ $active }) => $active ? '#ffffff' : 'rgba(255,255,255,0.5)'};
  cursor: pointer;
  padding: 2px 4px;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 0 40px;
`;

const HeroTitle = styled.h1`
  font-size: 84px;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.2;
  margin-bottom: 32px;
  letter-spacing: -2px;
  animation: ${fadeInUp} 0.8s ease-out;
`;

// 흰색 텍스트: background-clip sweep (글자에만 적용)
const WhiteText = styled.span`
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.5) 0%,
    rgba(255, 255, 255, 0.5) 25%,
    #ffffff 50%,
    rgba(255, 255, 255, 0.5) 75%,
    rgba(255, 255, 255, 0.5) 100%
  );
  background-size: 300% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${titleSweep} ${CYCLE}s ease-in-out 1.5s infinite;
`;

// 주황 글자: 한 글자씩 커지며 밝아짐
const HlChar = styled.span<{ $delay: number }>`
  display: inline-block;
  color: ${theme.colors.primary};
  transform-origin: center bottom;
  animation: ${hlCharPop} ${CYCLE}s ease-in-out ${({ $delay }) => 1.5 + $delay}s infinite;
`;

const HeroDesc = styled.p`
  font-size: 24px;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.6;
  margin-bottom: 56px;
  font-weight: 500;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
`;

const CustomizeBtn = styled.button`
  display: inline-flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  background: #F97316;
  color: #ffffff;
  font-size: 19px;
  font-weight: 600;
  padding: 20px 44px;
  border-radius: ${theme.layout.buttonRadius};
  border: none;
  cursor: pointer;
  transition: transform 0.25s ease, background 0.2s ease;
  animation: ${fadeInUp} 0.8s ease-out 0.4s both, ${pulse} 3s ease-in-out 1.5s infinite;

  &:hover {
    background: #EA580C;
    transform: scale(1.1) !important;
    transition: transform 0.4s ease, background 0.2s ease;
  }
`;

export default LandingPage;
